import React, { useEffect, useState } from "react";
import { Breadcrumb, Layout, Table, Button, Input, Space, Tag, Card, Typography, notification, DatePicker, Select, Modal } from "antd";
import { useAuth } from "../../hooks/useAuth";
import { FormattedMessage, useIntl } from "react-intl";
import { SearchOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from "react-router-dom";
import { IEvent } from "../../services/nestjs-base-api/types";
import { useGetAllEventsQuery } from "../../services/nestjs-base-api/admin-api";
import { GeneralStatusEnum } from "../../enums/GeneralStatusEnum";
import { Utils } from "../../utils/utils";
import dayjs from 'dayjs';

const { Content } = Layout;
const { Title } = Typography;
const { Search } = Input;
const { RangePicker } = DatePicker;

const AdminEventsPage: React.FunctionComponent = () => {
  const intl = useIntl();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [organisationFilter, setOrganisationFilter] = useState<number | null>(null);
  
  // Fetch events data
  const { data, error, isLoading } = useGetAllEventsQuery({ 
    jwtToken: user.jwtToken 
  }, { pollingInterval: 600000 });

  useEffect(() => {
    if (error) {
      if ('status' in error) {
        if (error.status === 401) {
          logout();
        }
      }
    }
  }, [error, logout]);

  const events = data || [];

  // Filter events based on search, date range, status, and organisation
  const filteredEvents = events.filter((event: IEvent) => {
    // Filter by search text
    const matchesSearch = searchText ? 
      event.name.toLowerCase().includes(searchText.toLowerCase()) ||
      (event.description && event.description.toLowerCase().includes(searchText.toLowerCase())) :
      true;
    
    // Filter by date range
    const eventDate = dayjs(event.date);
    const matchesDateRange = dateRange && dateRange[0] && dateRange[1] ? 
      eventDate.isAfter(dateRange[0]) && eventDate.isBefore(dateRange[1]) :
      true;
    
    // Filter by status
    const matchesStatus = statusFilter ? 
      event.status === statusFilter :
      true;
    
    // Filter by organisation
    const matchesOrganisation = organisationFilter ?
      (event.organisation && event.organisation.id === organisationFilter) || event.organisationId === organisationFilter :
      true;
    
    return matchesSearch && matchesDateRange && matchesStatus && matchesOrganisation;
  });

  // Handle event view
  const handleViewEvent = (id: number) => {
    navigate(`/admin/events/${id}`);
  };

  // Handle event deletion with confirmation
  const handleDeleteEvent = (id: number, name: string) => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'events.delete.confirm.title' }) || 'Delete Event',
      content: (
        <>
          <p>
            {intl.formatMessage({ id: 'events.delete.confirm.content' }) ||
              'This will permanently delete the event, all its galleries, and all photos. This action cannot be undone.'}
          </p>
          <p style={{ fontWeight: 'bold' }}>"{name}"</p>
        </>
      ),
      okText: intl.formatMessage({ id: 'common.delete' }) || 'Delete',
      cancelText: intl.formatMessage({ id: 'common.cancel' }) || 'Cancel',
      okButtonProps: { danger: true },
      onOk: () => {
        // In a real implementation, this would call the API to delete the event
        notification.success({
          message: intl.formatMessage({ id: 'admin.events.deleteSuccess' }) || 'Event Deleted',
          description: intl.formatMessage(
            { id: 'admin.events.deleteSuccessDesc' },
            { name }
          ) || `Event "${name}" has been successfully deleted.`,
        });
      },
    });
  };

  // Get unique organisations for filter and create a mapping of org ID to name
  const orgMap = events.reduce((map: Record<number, string>, event: any) => {
    // Handle both organisation object and organisationId
    const orgId = event.organisation?.id || event.organisationId;
    const orgName = event.organisation?.name || `Organisation ${orgId}`;

    if (orgId && !map[orgId]) {
      map[orgId] = orgName;
    }
    return map;
  }, {});

  // Convert to array of { id, name } for the dropdown
  const organisations = Object.entries(orgMap).map(([id, name]) => ({
    id: parseInt(id),
    name: name
  }));

  // Table columns
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a: IEvent, b: IEvent) => a.id - b.id,
      width: 80,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: IEvent, b: IEvent) => a.name.localeCompare(b.name),
    },
    {
      title: 'Organisation',
      dataIndex: 'organisation',
      key: 'organisation',
      render: (organisation: any, record: any) => {
        // Handle both nested organisation object from API and organisationId from interface
        if (organisation && organisation.name) {
          return organisation.name;
        } else if (record.organisationId) {
          return `Organisation ${record.organisationId}`;
        }
        return 'Unknown Organisation';
      },
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
      sorter: (a: IEvent, b: IEvent) => dayjs(a.date).unix() - dayjs(b.date).unix(),
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      render: (location: string) => location || '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: GeneralStatusEnum) => (
        <Tag color={status === GeneralStatusEnum.Active ? 'green' : 'red'}>
          {Utils.readableGeneralStatus(status)}
        </Tag>
      ),
    },
    {
      title: 'Galleries',
      key: 'galleries',
      render: (_: any, record: IEvent) => {
        // If the event has an organisation object, use that for navigation
        if (record.organisation && record.organisation.id) {
          return (
            <Button
              type="link"
              onClick={() => navigate(`/admin/organisations/${record.organisation?.id}/events/${record.id}/galleries`)}
            >
              View Galleries
            </Button>
          );
        }
        // Fallback to the old route if organisation isn't present
        return (
          <Button
            type="link"
            onClick={() => navigate(`/admin/events/${record.id}/galleries`)}
          >
            View Galleries
          </Button>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: IEvent) => (
        <Space size="small">
          <Button 
            icon={<EyeOutlined />} 
            onClick={() => handleViewEvent(record.id)} 
            title="View"
          />
          <Button 
            danger 
            icon={<DeleteOutlined />} 
            onClick={() => handleDeleteEvent(record.id, record.name)} 
            title="Delete"
          />
        </Space>
      ),
    },
  ];

  return (
    <Content style={{ margin: '0 16px' }}>
      <Breadcrumb style={{ margin: '16px 0' }}>
        <Breadcrumb.Item>RecogniX</Breadcrumb.Item>
        <Breadcrumb.Item>
          <FormattedMessage id={'section.adminDashboardName'} defaultMessage="Admin Dashboard" />
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <FormattedMessage id={'section.eventsName'} defaultMessage="Events" />
        </Breadcrumb.Item>
      </Breadcrumb>
      
      <Card
        title={
          <Title level={2}>
            <FormattedMessage id={'admin.events.title'} defaultMessage="Manage Events" />
          </Title>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
            <Search
              placeholder={intl.formatMessage({ id: 'admin.events.searchPlaceholder' }) || "Search events by name or description"}
              allowClear
              enterButton={<SearchOutlined />}
              onSearch={value => setSearchText(value)}
              style={{ width: 300 }}
            />
            
            <Space>
              <RangePicker 
                onChange={(dates) => setDateRange(dates as [dayjs.Dayjs | null, dayjs.Dayjs | null])}
                placeholder={[
                  intl.formatMessage({ id: 'admin.events.startDate' }) || "Start Date",
                  intl.formatMessage({ id: 'admin.events.endDate' }) || "End Date"
                ]}
              />
              
              <Select
                placeholder={intl.formatMessage({ id: 'admin.events.statusFilter' }) || "Filter by status"}
                style={{ width: 150 }}
                allowClear
                onChange={(value) => setStatusFilter(value)}
              >
                <Select.Option value={GeneralStatusEnum.Active}>Active</Select.Option>
                <Select.Option value={GeneralStatusEnum.Inactive}>Inactive</Select.Option>
              </Select>
              
              <Select
                placeholder={intl.formatMessage({ id: 'admin.events.organisationFilter' }) || "Filter by organisation"}
                style={{ width: 180 }}
                allowClear
                onChange={(value) => setOrganisationFilter(value)}
              >
                {organisations.map((org) => (
                  <Select.Option key={org.id} value={org.id}>
                    {org.name}
                  </Select.Option>
                ))}
              </Select>
            </Space>
          </div>
          
          <Table 
            dataSource={filteredEvents} 
            columns={columns} 
            rowKey="id"
            loading={isLoading}
            pagination={{ pageSize: 10 }}
          />
        </Space>
      </Card>
    </Content>
  );
};

export default AdminEventsPage;